<template>
  <v-app>
    <core-toolbar v-if="isAuthenticated" />

    <core-drawer v-if="isAuthenticated" />

    <core-view />
  </v-app>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import axios from 'axios'
import qs from 'qs'
import { CHECK_AUTH } from './store/actions/auth'
import ApiService from './utils/api.service'
import JwtService from './utils/jwt.service'
import { USER_ME_REQUEST } from './store/actions/user'

export default {
  name: 'App',
  computed: {
    ...mapGetters('auth', ['isAuthenticated'])
  },
  mounted () {
    if (this.isAuthenticated) {
      this.USER_ME_REQUEST()
    }
  },
  data () {
    return {
      isRefreshing: false,
      refreshSubscribers: []
    }
  },
  created: function () {
    ApiService.setHeader()
    axios.defaults.paramsSerializer = params => {
      return qs.stringify(params, { arrayFormat: 'repeat' })
    }
    axios.interceptors.request.use(conf => {
      return conf
    }, err => {
      return Promise.reject(err)
    })
    const self = this
    axios.interceptors.response.use(
      response => {
        return response
      },
      err => {
        const originalRequest = err.config

        if (err?.config?.url && err.config.url.includes('auth/oauth2/token')) {
          return Promise.reject(err)
        }
        const status = err.response ? err.response.status : null
        if (status === 401 && !originalRequest._retry) {
          originalRequest._retry = true
          if (!this.isRefreshing) {
            if (this.$store.state.auth.refreshToken) {
              this.isRefreshing = true
              return self.CHECK_AUTH().then(() => {
                const token = JwtService.getToken()
                this.isRefreshing = false
                self.onRefreshed(token)
                return self.retryWithToken(originalRequest, token)
              }, (errorRefresh) => {
                this.isRefreshing = false
                self.onRefreshFailed(errorRefresh)
                if (!this.isAuthenticated) {
                  this.$router.push('/login')
                }
                return Promise.reject(errorRefresh)
              })
            } else {
              this.$router.push('/login')
              return Promise.reject(err)
            }
          }
          return new Promise((resolve, reject) => {
            self.subscribeTokenRefresh({
              onToken: token => resolve(self.retryWithToken(originalRequest, token)),
              onFailure: reject
            })
          })
        } else if (status === 422) {
          const { messages, message } = err.response.data
          if (messages) self.processError(messages)
          else if (message && !err.response.data.errors) self.$toast.error(message)  // a body with `errors` is shown on the form's fields
          err.handled = true
          return Promise.reject(err)
        } else if (status === 400) {
          const message = self.$apiErrorMessage(err.response.data && err.response.data.message)
          if (message) {
            self.$toast.error(message)
            err.handled = true
          }
          return Promise.reject(err)
        }
        if (!err.response && !axios.isCancel(err)) {
          // network error
          self.$toast.error(self.$t('Common.error.network_error'))
          err.handled = true
        }
        return Promise.reject(err)
      }
    )
  },
  methods: {
    // axios 1.x exposes the failed request's headers as an AxiosHeaders instance that
    // does not survive a re-dispatch: assigning Authorization on it is silently dropped
    // and the retry leaves without any credentials. Handing axios a plain object lets it
    // rebuild the headers from its defaults and actually send the new token.
    retryWithToken (config, token) {
      return axios.request({ ...config, headers: { Authorization: 'Bearer ' + token } })
    },
    onRefreshed (token) {
      const subscribers = this.refreshSubscribers
      this.refreshSubscribers = []
      subscribers.forEach(subscriber => subscriber.onToken(token))
    },
    onRefreshFailed (error) {
      const subscribers = this.refreshSubscribers
      this.refreshSubscribers = []
      subscribers.forEach(subscriber => subscriber.onFailure(error))
    },
    subscribeTokenRefresh (subscriber) {
      this.refreshSubscribers.push(subscriber)
    },
    processError (messages) {
      Object.keys(messages).forEach((key) => {
        const errors = messages[key]
        const name = typeof this.$t(`Common.${key}`) === "string" ? this.$t(`Common.${key}`) : this.$t(`Common.${key}.name`)
        if (Array.isArray(errors)) {
          this.$toast.error(`${name}: ${errors.join(',')}`)
        } else {
          this.processError(errors)
        }
      })
    },
    ...mapActions('auth', [
      CHECK_AUTH
    ]),
    ...mapActions('user', [
      USER_ME_REQUEST
    ])
  }
}
</script>

<style lang="scss">
    @import '@/styles/index.scss';

    /* Remove in 1.2 */
    .v-datatable thead th.column.sortable i {
        vertical-align: unset;
    }
</style>
